/* TODO: t() */
import React, { useReducer } from 'react';
import { StyleSheet, View } from 'react-native';

import connectData, { ConnectedProps } from '../../support/connectData';
import { showError, showSuccess } from '../App/AppAlert';
import {
  Page,
  Card,
  Text,
  Link,
  BooleanInput,
  Button,
  ButtonToolbar,
  RadioItem,
  RadioGroup,
  TextInput,
} from '../uiold';
import {
  DEFAULT_SETTINGS,
  DEFAULT_PAYMENT_PROVIDER,
} from '../../support/shared/constants/Constants';
import { postJSON } from '../../support/Fetch';
import ensureObject from '../../support/shared/ensureObject';
import AccountActions from '../../support/AccountActions';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { revokeAuth } from '../Settings/stripe/helpers';
import StripeConnect from '../Settings/stripe/StripeConnect';
import Editor, {
  EditorContent,
  createEditorContent,
} from '../../core-ui/Editor/StatefulEditor';
import { canUser } from '../../support/shared/permissionHelpers';

import { User } from '../../types/shared/User';
import {
  Account,
  Settings,
  LinkedAccounts,
  PaymentProviders,
} from '../../types/shared/Account';

type PaymentSettingsType = {
  alternativeEnabled: boolean;
  payment_provider: PaymentProviders;
  content: EditorContent;
  businessInfo: EditorContent;
  paymentspring_public_api_key: string;
  paymentspring_private_api_key: string;
};

type Props = {
  onContentChanged: (type: string, value: string) => void;
};

type State = {
  account: Account;
  alternativeEnabled: boolean;
  payment_provider: PaymentProviders;
  content: EditorContent;
  isSaving: boolean;
  businessInfo: EditorContent;
  paymentspring_public_api_key: string;
  paymentspring_private_api_key: string;
  resetCount: number;
};

type Action =
  | { type: 'TOGGLE_ALTERNATE_PAYMENT'; alternativeEnabled: boolean }
  | { type: 'CONTENT_CHANGE'; content: EditorContent }
  | { type: 'BUSINESS_INFO_CHANGE'; businessInfo: EditorContent }
  | { type: 'CHANGE_PAYMENT_PROVIDER'; payment_provider: PaymentProviders }
  | {
      type: 'PAYMENTSPRING_PUBLIC_API_CHANGE';
      paymentspring_public_api_key: string;
    }
  | {
      type: 'PAYMENTSPRING_PRIVATE_API_CHANGE';
      paymentspring_private_api_key: string;
    }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_COMPLETE' }
  | { type: 'RESET_FORM' };

function contentReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'TOGGLE_ALTERNATE_PAYMENT': {
      let { alternativeEnabled } = action;
      return { ...state, alternativeEnabled };
    }
    case 'CHANGE_PAYMENT_PROVIDER': {
      let { payment_provider } = action;
      return { ...state, payment_provider };
    }
    case 'PAYMENTSPRING_PUBLIC_API_CHANGE': {
      let { paymentspring_public_api_key } = action;
      return {
        ...state,
        paymentspring_public_api_key: paymentspring_public_api_key,
      };
    }
    case 'PAYMENTSPRING_PRIVATE_API_CHANGE': {
      let { paymentspring_private_api_key } = action;
      return {
        ...state,
        paymentspring_private_api_key: paymentspring_private_api_key,
      };
    }
    case 'CONTENT_CHANGE': {
      let { content } = action;
      // isChanged will always be true because getApprovalEmail().emailContent
      // is a new instance of EditorContent each time.
      let isChanged = getPaymentSettings(state.account).content !== content;
      return isChanged ? { ...state, content } : state;
    }
    case 'BUSINESS_INFO_CHANGE': {
      let { businessInfo } = action;
      let isChanged =
        getPaymentSettings(state.account).businessInfo !== businessInfo;
      return isChanged ? { ...state, businessInfo } : state;
    }
    case 'SAVE_START': {
      return { ...state, isSaving: true };
    }
    case 'SAVE_COMPLETE': {
      return { ...state, isSaving: false };
    }
    case 'RESET_FORM': {
      let {
        alternativeEnabled,
        content,
        businessInfo,
        payment_provider,
        paymentspring_public_api_key,
        paymentspring_private_api_key,
      } = getPaymentSettings(state.account);
      return {
        ...state,
        alternativeEnabled,
        content,
        businessInfo,
        payment_provider,
        paymentspring_public_api_key,
        paymentspring_private_api_key,
        // This is incremented to trigger unmount/remount on stateful
        // components.
        resetCount: state.resetCount + 1,
      };
    }
    default: {
      return state;
    }
  }
}

async function saveChanges(state: State, dispatch: (action: Action) => void) {
  let {
    account,
    alternativeEnabled,
    content,
    businessInfo,
    payment_provider,
    paymentspring_public_api_key,
    paymentspring_private_api_key,
  } = state;
  let alternative_payment = {
    enabled: alternativeEnabled,
    content: content.toString(),
  };

  let settingsUpdates: Settings = {
    alternative_payment,
    payment_provider,
    billing: { businessInfo: businessInfo.toString() },
  };
  let linkedAccountsUpdates: LinkedAccounts = {};

  if (payment_provider === 'paymentspring') {
    let paymentSpring = {
      public_api_key: paymentspring_public_api_key ?? '',
      private_api_key:
        account.linked_accounts?.paymentSpring?.private_api_key ?? false,
    };
    if (paymentspring_private_api_key) {
      paymentSpring.private_api_key = true;
      await postJSON(`/api/paymentSpring/updatePrivateApiKey`, {
        paymentspring_private_api_key,
        accountID: account.id,
      });
      dispatch({
        type: 'PAYMENTSPRING_PRIVATE_API_CHANGE',
        paymentspring_private_api_key: '',
      });
    }
    linkedAccountsUpdates.paymentSpring = paymentSpring;
    AccountActions.updateLinkedAccounts({ paymentSpring });
  }

  let accountID = account.id;

  dispatch({ type: 'SAVE_START' });

  await postJSON(
    `/api/accounts/${accountID}/settings`,
    {
      account: {
        settings: settingsUpdates,
        linked_accounts: linkedAccountsUpdates,
      },
    },
    (error, data) => {
      if (error) {
        let errorObject = ensureObject(error);
        let errorMessage = errorObject
          ? String(errorObject.message)
          : String(error);
        showError(`Unable to complete request: ${errorMessage}`);
        return;
      }
      if (data.success) {
        AccountActions.updateSettings(settingsUpdates);
        showSuccess(`Saved Successfully.`);
      }
      dispatch({ type: 'SAVE_COMPLETE' });
    },
  );
}

function PaymentSettings(props: Props & { account: Account; user: User }) {
  let { account, user } = props;
  let initialState = React.useMemo(() => getInitialState(account), [account]);
  let [state, dispatch] = useReducer(contentReducer, initialState);

  let {
    isSaving,
    alternativeEnabled,
    content,
    businessInfo,
    payment_provider,
    paymentspring_public_api_key,
    paymentspring_private_api_key,
    resetCount,
  } = state;
  let canUserWrite = user && canUser(user, { settings: 'WRITE' });

  let renderSaveControls = () => {
    let {
      alternativeEnabled: oldEnabled,
      content: oldContent,
      businessInfo: oldBusinessInfo,
      payment_provider: oldPaymentProvider,
      paymentspring_public_api_key: oldPaymentSpringPublicApiKey,
      paymentspring_private_api_key: oldPaymentSpringPrivateApiKey,
    } = getPaymentSettings(account);
    if (
      oldEnabled === alternativeEnabled &&
      content === oldContent &&
      businessInfo === oldBusinessInfo &&
      payment_provider === oldPaymentProvider &&
      paymentspring_public_api_key === oldPaymentSpringPublicApiKey &&
      paymentspring_private_api_key === oldPaymentSpringPrivateApiKey
    ) {
      return null;
    }
    return isSaving ? (
      <LoadingIndicator />
    ) : (
      <ButtonToolbar>
        {canUserWrite && (
          <>
            <Button
              title="Revert"
              onPress={() => dispatch({ type: 'RESET_FORM' })}
            />
            <Button
              title="Save"
              onPress={async () => saveChanges(state, dispatch)}
              isPrimary
            />
          </>
        )}
      </ButtonToolbar>
    );
  };

  return (
    <Page>
      <Page.Header title="Payment Settings">{renderSaveControls()}</Page.Header>
      <Text style={styles.description}>
        Please go to the billing section and create an invoice to send a bill to
        your Vendors.
      </Text>
      <Card style={styles.marginVertical}>
        <Text style={styles.header}>Online Payments</Text>
        <Text style={styles.description}>
          MarketWurks supports integration with <StripeLink /> or{' '}
          <Link href="https://signup.paymentspring.com/instances?product_id=88c4a9cf-abe6-4701-8e6c-fa0a15551e04">
            PaymentSpring
          </Link>
          {'. '}
          If you have an account with one of these providers you can connect it
          with MarketWurks allowing your vendors to easily pay you.
        </Text>
        <Text style={styles.subHeader}>Selected Online Payment Provider</Text>
        <ProviderOptions
          value={payment_provider}
          onChange={(value) =>
            canUserWrite &&
            dispatch({
              type: 'CHANGE_PAYMENT_PROVIDER',
              payment_provider: value,
            })
          }
        />
        {payment_provider === 'stripe' ? (
          <>
            <Text style={styles.subHeader}>Connect Stripe Account</Text>
            <StripeConnect account={account}>
              <Text>
                Your Stripe account is connected.{' '}
                <Link onPress={revokeAuth}>Click here to disconnect</Link>.
              </Text>
            </StripeConnect>
          </>
        ) : null}
        {payment_provider === 'paymentspring' ? (
          <>
            <Text style={styles.subHeader}>Connect PaymentSpring Account</Text>
            <Text style={styles.description}>
              <Link href="https://signup.paymentspring.com/instances?product_id=88c4a9cf-abe6-4701-8e6c-fa0a15551e04">
                Click here to create
              </Link>{' '}
              your PaymentSpring account
            </Text>

            <Text style={styles.description}>
              Log in to your PaymentSpring account and copy both your public and
              private API key from your account settings into the fields below.
              Keys will not be saved in the fields below unless they are valid.
            </Text>
            <Text style={styles.label}>Public API Key</Text>
            <View style={styles.rowFlexed}>
              <TextInput
                style={{ maxWidth: 400 }}
                disabled={!canUserWrite}
                onChangeText={(value) => {
                  dispatch({
                    type: 'PAYMENTSPRING_PUBLIC_API_CHANGE',
                    paymentspring_public_api_key: value,
                  });
                }}
                value={paymentspring_public_api_key ?? ''}
              />
            </View>
            <Text style={styles.label}>Private API Key</Text>
            <View style={styles.rowFlexed}>
              <TextInput
                style={{ maxWidth: 400 }}
                disabled={!canUserWrite}
                placeholder={
                  account.linked_accounts?.paymentSpring?.private_api_key
                    ? '**** (leave blank to keep key unchanged)'
                    : ''
                }
                onChangeText={(value) => {
                  dispatch({
                    type: 'PAYMENTSPRING_PRIVATE_API_CHANGE',
                    paymentspring_private_api_key: value,
                  });
                }}
                value={paymentspring_private_api_key ?? ''}
              />
            </View>
          </>
        ) : null}
      </Card>
      <Card style={styles.marginVertical}>
        <Text style={styles.header}>Alternative Payment Instructions</Text>
        <Text style={styles.marginVertical}>Enable Alternative Payment</Text>
        <BooleanInput
          value={alternativeEnabled}
          onChange={(value) => {
            canUserWrite &&
              dispatch({
                type: 'TOGGLE_ALTERNATE_PAYMENT',
                alternativeEnabled: value,
              });
          }}
        />
        {alternativeEnabled ? (
          <>
            <Text style={styles.description}>
              If you fill in the following field, this will be given as an
              option for alternative payment.
            </Text>
            <Editor
              key={resetCount}
              height={320}
              initialContent={initialState.content}
              onChange={(content) =>
                dispatch({ type: 'CONTENT_CHANGE', content })
              }
              readOnly={!canUserWrite}
            />
          </>
        ) : null}
      </Card>
      <Card style={styles.marginVertical}>
        <Text style={styles.header}>Invoice Address</Text>
        <Text style={styles.description}>
          If you fill in the following field, this will appear as the address
          header under the invoice title that is generated
        </Text>
        <Editor
          height={320}
          initialContent={initialState.businessInfo}
          onChange={(businessInfo) => {
            dispatch({ type: 'BUSINESS_INFO_CHANGE', businessInfo });
          }}
          readOnly={!canUserWrite}
        />
      </Card>
      <View style={{ height: 40 }} />
    </Page>
  );
}

let providers: Record<PaymentProviders, string> = {
  none: 'None',
  stripe: 'Stripe',
  paymentspring: 'PaymentSpring',
};

function ProviderOptions(props: {
  value: PaymentProviders;
  onChange: (value: PaymentProviders) => void;
}) {
  let { value, onChange } = props;
  return (
    <RadioGroup
      onChange={(value) => {
        for (let key of Object.keys(providers)) {
          if (key === value) {
            onChange(key);
          }
        }
      }}
      selectedValue={value}
      style={styles.rowFlexed}
    >
      {Object.entries(providers).map(([key, name]) => (
        <RadioItem
          style={styles.radioItem}
          title={name}
          key={key}
          value={key}
        />
      ))}
    </RadioGroup>
  );
}

let styles = StyleSheet.create({
  header: {
    marginVertical: 8,
    fontSize: 18,
    fontWeight: '700',
  },
  subHeader: {
    marginTop: 16,
    marginBottom: 8,
    fontSize: 16,
    fontWeight: '600',
  },
  description: {
    marginVertical: 8,
  },
  marginVertical: {
    marginVertical: 12,
  },
  rowFlexed: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  radioItem: {
    marginRight: 4,
  },
  label: {
    marginVertical: 4,
  },
});

function StripeLink() {
  return <Link href="https://stripe.com/">Stripe</Link>;
}

function getInitialState(account: Account): State {
  let {
    alternativeEnabled: enabled,
    content,
    businessInfo,
    payment_provider,
    paymentspring_public_api_key,
    paymentspring_private_api_key,
  } = getPaymentSettings(account);
  return {
    account,
    alternativeEnabled: enabled,
    content,
    isSaving: false,
    businessInfo,
    payment_provider,
    paymentspring_public_api_key,
    paymentspring_private_api_key: paymentspring_private_api_key,
    resetCount: 0,
  };
}

function getPaymentSettings(account: Account): PaymentSettingsType {
  if (account.settings && account.settings.alternative_payment) {
    let { enabled, content } = account.settings.alternative_payment;
    let businessInfo = account.settings.billing?.businessInfo;
    let paymentspring_public_api_key =
      account.linked_accounts?.paymentSpring?.public_api_key;
    return {
      payment_provider:
        account.settings.payment_provider ?? DEFAULT_PAYMENT_PROVIDER,
      alternativeEnabled: enabled,
      content: createEditorContent(content),
      businessInfo: createEditorContent(businessInfo ?? ''),
      paymentspring_public_api_key: paymentspring_public_api_key ?? '',
      paymentspring_private_api_key: '',
    };
  } else {
    let { enabled, content } = DEFAULT_SETTINGS.alternative_payment;
    let { businessInfo } = DEFAULT_SETTINGS.billing;
    return {
      alternativeEnabled: enabled,
      content: createEditorContent(content),
      businessInfo: createEditorContent(businessInfo),
      payment_provider: DEFAULT_PAYMENT_PROVIDER,
      paymentspring_public_api_key: '',
      paymentspring_private_api_key: '',
    };
  }
}

function PaymentSettingsWrapper(props: FullProps) {
  let { account, user, ...otherProps } = props;
  if (account && user) {
    return <PaymentSettings account={account} user={user} {...otherProps} />;
  } else {
    // TODO: Show loading indicator?
    return null;
  }
}

const connector = connectData<Props>()(() => ({
  account: { dataStoreKey: 'account' },
  user: { dataStoreKey: 'user' },
}));
type FullProps = ConnectedProps<typeof connector>;
export default connector(PaymentSettingsWrapper);
